import React, { useEffect } from 'react';
import {
  Modal,
  Form,
  Button,
  Alert,
  Space,
  Collapse,
  Typography,
  Input,
  Select,
  Switch,
  InputNumber,
  Divider,
  Row,
  Col,
  Checkbox,
  Slider,
  Radio,
  Tooltip,
} from 'antd';
import { SettingOutlined, InfoCircleOutlined } from '@ant-design/icons';

const { Panel } = Collapse;
const { Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// URL/ドメインからドメイン部分のみを抽出するヘルパー
const extractDomainFromLine = (line) => {
  const trimmed = (line || '').trim();
  if (!trimmed) return '';
  
  // TLD指定（.gov, .edu など）はそのまま
  if (/^\.[a-z]{2,}$/i.test(trimmed)) return trimmed;
  
  // URLパターンの場合、ドメインを抽出
  try {
    if (trimmed.includes('://') || trimmed.startsWith('www.')) {
      const urlStr = trimmed.startsWith('www.') ? `https://${trimmed}` : trimmed;
      const url = new URL(urlStr);
      return url.hostname.replace(/^www\./, '');
    }
  } catch (_) {
    // URL解析失敗時はそのまま
  }
  
  // パス付きドメイン（example.com/path）の場合
  const slashIdx = trimmed.indexOf('/');
  if (slashIdx > 0) {
    return trimmed.substring(0, slashIdx).replace(/^www\./, '');
  }
  
  // それ以外はそのまま返す
  return trimmed.replace(/^www\./, '');
};

// 複数行のテキストをドメインリストに変換
const processDomainInput = (text) => {
  if (!text) return '';
  return text
    .split('\n')
    .map(line => extractDomainFromLine(line))
    .filter(d => d)
    .join('\n');
};

const StyleOverrideModal = ({
  visible,
  onCancel,
  onSave,
  baseStyle,
  styleName,
  currentOverrides,
}) => {
  const [form] = Form.useForm();

  // baseStyleから初期値を取得するヘルパー
  const getNested = (obj, path, defaultValue = undefined) => {
    if (!obj) return defaultValue;
    const keys = path.split('.');
    let current = obj;
    for (const key of keys) {
      if (current === null || current === undefined) return defaultValue;
      current = current[key];
    }
    return current !== undefined ? current : defaultValue;
  };

  // スタイル管理画面と同じマッピング関数
  const getRangeMid = s => {
    const t = String(s || '').trim();
    if (/^\d+$/.test(t)) return Number(t);
    if (!t.includes('-')) return NaN;
    const parts = t.split('-').map(x => Number(String(x).trim()));
    if (parts.length < 2) return NaN;
    const a = Number.isFinite(parts[0]) ? parts[0] : NaN;
    const b = Number.isFinite(parts[1]) ? parts[1] : a;
    if (!Number.isFinite(a) && !Number.isFinite(b)) return NaN;
    const lo =
      Number.isFinite(a) && Number.isFinite(b)
        ? Math.min(a, b)
        : Number.isFinite(a)
          ? a
          : b;
    const hi =
      Number.isFinite(a) && Number.isFinite(b)
        ? Math.max(a, b)
        : Number.isFinite(a)
          ? a
          : b;
    const v = (lo + hi) / 2;
    return Number.isFinite(v) ? v : NaN;
  };

  const mapBulletsPreset = s => {
    const mid = getRangeMid(s);
    if (!Number.isFinite(mid)) return 'medium';
    if (mid < 1) return 'very_low'; // 0-1
    if (mid < 1.5) return 'low'; // 1-2
    if (mid < 2.5) return 'slightly_low'; // 2-3
    if (mid <= 3) return 'medium'; // 2-4 (mid=3)
    if (mid <= 5) return 'slightly_high'; // 4-6 (mid=5)
    if (mid <= 6.5) return 'high'; // 5-8 (mid=6.5)
    return 'very_high'; // 6-8 (mid=7)
  };

  const mapTablesPreset = s => {
    const t = String(s || '').trim();
    let mid = NaN;
    if (/^\d+$/.test(t)) {
      mid = Number(t);
    } else if (t.includes('-')) {
      const parts = t.split('-').map(x => Number(String(x).trim()));
      if (parts.length >= 2) {
        const a = Number.isFinite(parts[0]) ? parts[0] : NaN;
        const b = Number.isFinite(parts[1]) ? parts[1] : a;
        if (Number.isFinite(a) || Number.isFinite(b)) {
          const lo =
            Number.isFinite(a) && Number.isFinite(b)
              ? Math.min(a, b)
              : Number.isFinite(a)
                ? a
                : b;
          const hi =
            Number.isFinite(a) && Number.isFinite(b)
              ? Math.max(a, b)
              : Number.isFinite(a)
                ? a
                : b;
          mid = (lo + hi) / 2;
        }
      }
    }
    if (!Number.isFinite(mid)) return 'medium';
    if (mid < 0.5) return 'very_low'; // 0-1 (mid=0.5)
    if (mid < 1.5) return 'low'; // 1-2 (mid=1.5)
    if (mid < 2) return 'slightly_low'; // 1-2 (mid=1.5)
    if (mid <= 2.5) return 'medium'; // 2-3 (mid=2.5)
    if (mid <= 3.5) return 'slightly_high'; // 3-4 (mid=3.5)
    if (mid <= 5) return 'high'; // 4-6 (mid=5)
    return 'very_high'; // 5-7 (mid=6)
  };

  // モーダルが開いたとき、フォームを初期化
  useEffect(() => {
    if (visible && baseStyle) {
      // allow_symbolsをui_heading_symbols形式に変換
      const allowSymbols = getNested(
        baseStyle,
        'headings_style.temperature.allow_symbols',
      );
      const headingSymbols = [];
      if (allowSymbols) {
        if (allowSymbols.question) headingSymbols.push('question');
        if (allowSymbols.brackets) headingSymbols.push('brackets');
        if (allowSymbols.colon) headingSymbols.push('colon');
      }

      // フォームの初期値を設定
      form.setFieldsValue({
        // 見出し設定
        h2_typical:
          currentOverrides?.headings_style?.counts?.h2_per_article?.typical ||
          getNested(
            baseStyle,
            'headings_style.counts.h2_per_article.typical',
          ) ||
          '',
        h3_typical:
          currentOverrides?.headings_style?.counts?.h3_per_h2?.typical ||
          getNested(baseStyle, 'headings_style.counts.h3_per_h2.typical') ||
          '',
        h2_chars_typical:
          currentOverrides?.headings_style?.length_policy?.h2_chars?.typical ||
          getNested(
            baseStyle,
            'headings_style.length_policy.h2_chars.typical',
          ) ||
          '',
        h3_chars_typical:
          currentOverrides?.headings_style?.length_policy?.h3_chars?.typical ||
          getNested(
            baseStyle,
            'headings_style.length_policy.h3_chars.typical',
          ) ||
          '',
        h2_body_chars_typical:
          currentOverrides?.headings_style?.length_policy?.h2_body_chars
            ?.typical ||
          getNested(
            baseStyle,
            'headings_style.length_policy.h2_body_chars.typical',
          ) ||
          '',
        h3_body_chars_typical:
          currentOverrides?.headings_style?.length_policy?.h3_body_chars
            ?.typical ||
          getNested(
            baseStyle,
            'headings_style.length_policy.h3_body_chars.typical',
          ) ||
          '',
        ui_allow_interrogative:
          currentOverrides?.headings_style?.temperature?.allow_interrogative ??
          getNested(
            baseStyle,
            'headings_style.temperature.allow_interrogative',
          ) ??
          false,
        ui_heading_symbols: currentOverrides?.headings_style?.temperature
          ?.allow_symbols
          ? (() => {
              const symbols = [];
              const override =
                currentOverrides.headings_style.temperature.allow_symbols;
              if (override.question) symbols.push('question');
              if (override.brackets) symbols.push('brackets');
              if (override.colon) symbols.push('colon');
              return symbols;
            })()
          : headingSymbols,

        // 装飾（箇条書き/表）
        bullets_preset:
          currentOverrides?.formatting_style?.bullets?.preset ||
          mapBulletsPreset(
            getNested(
              baseStyle,
              'prose_style.formatting.bullets.frequency_per_1000w.typical',
            ),
          ),
        tables_preset:
          currentOverrides?.formatting_style?.tables?.preset ||
          mapTablesPreset(
            getNested(
              baseStyle,
              'prose_style.formatting.tables.frequency_per_1000w.typical',
            ),
          ),
        bullets_max_nesting:
          currentOverrides?.formatting_style?.bullets?.max_nesting ??
          getNested(
            baseStyle,
            'prose_style.formatting.rules.bullets.max_nesting',
          ) ??
          1,
        bullets_item_max_chars:
          currentOverrides?.formatting_style?.bullets?.item_max_chars ??
          getNested(
            baseStyle,
            'prose_style.formatting.rules.bullets.item_max_chars',
          ) ??
          80,
        bullets_terminal_punct_disallowed:
          currentOverrides?.formatting_style?.bullets
            ?.terminal_punct_disallowed ??
          getNested(
            baseStyle,
            'prose_style.formatting.rules.bullets.terminal_punct_disallowed',
          ) ??
          false,
        bullets_parallelism_required:
          currentOverrides?.formatting_style?.bullets?.parallelism_required ??
          getNested(
            baseStyle,
            'prose_style.formatting.rules.bullets.parallelism_required',
          ) ??
          false,

        // カスタムルール
        additional_instructions_outline:
          currentOverrides?.custom_rules?.additional_instructions?.outline ??
          getNested(
            baseStyle,
            'prose_style.constraints.additional_instructions_outline',
          ) ??
          '',
        additional_instructions_content:
          currentOverrides?.custom_rules?.additional_instructions?.content ??
          getNested(
            baseStyle,
            'prose_style.constraints.additional_instructions_content',
          ) ??
          '',
        avoid_text:
          currentOverrides?.custom_rules?.avoid?.text ??
          ((
            getNested(baseStyle, 'prose_style.constraints.avoid_patterns') || []
          ).join('\n') ||
            ''),
        rewrite_text: currentOverrides?.custom_rules?.rewrite?.text ?? '',
        // 調査フィルター
        domain_filter_mode: 
          currentOverrides?.domain_filter_override?.mode ??
          getNested(baseStyle, 'domain_filter.mode') ?? 
          'disabled',
        domain_filter_whitelist: 
          currentOverrides?.domain_filter_override?.whitelist?.join('\n') ??
          (getNested(baseStyle, 'domain_filter.whitelist') || []).join('\n') ??
          '',
        domain_filter_blacklist: 
          currentOverrides?.domain_filter_override?.blacklist?.join('\n') ??
          (getNested(baseStyle, 'domain_filter.blacklist') || []).join('\n') ??
          '',
      });
    }
  }, [visible, baseStyle, currentOverrides, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      // 変更された項目のみを抽出してオーバーライドオブジェクトを作成
      const overrides = {};

      // 見出し設定
      const headingsOverride = {};
      if (
        values.h2_typical !==
        getNested(baseStyle, 'headings_style.counts.h2_per_article.typical')
      ) {
        if (!headingsOverride.counts) headingsOverride.counts = {};
        if (!headingsOverride.counts.h2_per_article)
          headingsOverride.counts.h2_per_article = {};
        headingsOverride.counts.h2_per_article.typical = values.h2_typical;
      }
      if (
        values.h3_typical !==
        getNested(baseStyle, 'headings_style.counts.h3_per_h2.typical')
      ) {
        if (!headingsOverride.counts) headingsOverride.counts = {};
        if (!headingsOverride.counts.h3_per_h2)
          headingsOverride.counts.h3_per_h2 = {};
        headingsOverride.counts.h3_per_h2.typical = values.h3_typical;
      }
      if (
        values.h2_chars_typical !==
        getNested(baseStyle, 'headings_style.length_policy.h2_chars.typical')
      ) {
        if (!headingsOverride.length_policy)
          headingsOverride.length_policy = {};
        if (!headingsOverride.length_policy.h2_chars)
          headingsOverride.length_policy.h2_chars = {};
        headingsOverride.length_policy.h2_chars.typical =
          values.h2_chars_typical;
      }
      if (
        values.h3_chars_typical !==
        getNested(baseStyle, 'headings_style.length_policy.h3_chars.typical')
      ) {
        if (!headingsOverride.length_policy)
          headingsOverride.length_policy = {};
        if (!headingsOverride.length_policy.h3_chars)
          headingsOverride.length_policy.h3_chars = {};
        headingsOverride.length_policy.h3_chars.typical =
          values.h3_chars_typical;
      }
      if (
        values.h2_body_chars_typical !==
        getNested(
          baseStyle,
          'headings_style.length_policy.h2_body_chars.typical',
        )
      ) {
        if (!headingsOverride.length_policy)
          headingsOverride.length_policy = {};
        if (!headingsOverride.length_policy.h2_body_chars)
          headingsOverride.length_policy.h2_body_chars = {};
        headingsOverride.length_policy.h2_body_chars.typical =
          values.h2_body_chars_typical;
      }
      if (
        values.h3_body_chars_typical !==
        getNested(
          baseStyle,
          'headings_style.length_policy.h3_body_chars.typical',
        )
      ) {
        if (!headingsOverride.length_policy)
          headingsOverride.length_policy = {};
        if (!headingsOverride.length_policy.h3_body_chars)
          headingsOverride.length_policy.h3_body_chars = {};
        headingsOverride.length_policy.h3_body_chars.typical =
          values.h3_body_chars_typical;
      }
      // 見出し設定の疑問文と許可記号
      const baseAllowInterrogative = getNested(
        baseStyle,
        'headings_style.temperature.allow_interrogative',
      );
      if (values.ui_allow_interrogative !== baseAllowInterrogative) {
        if (!headingsOverride.temperature) headingsOverride.temperature = {};
        headingsOverride.temperature.allow_interrogative =
          values.ui_allow_interrogative;
      }

      // 許可記号の比較（配列→オブジェクト形式に変換）
      const baseAllowSymbols =
        getNested(baseStyle, 'headings_style.temperature.allow_symbols') || {};
      const currentSymbols = {
        question: values.ui_heading_symbols?.includes('question') || false,
        brackets: values.ui_heading_symbols?.includes('brackets') || false,
        colon: values.ui_heading_symbols?.includes('colon') || false,
      };
      if (JSON.stringify(currentSymbols) !== JSON.stringify(baseAllowSymbols)) {
        if (!headingsOverride.temperature) headingsOverride.temperature = {};
        headingsOverride.temperature.allow_symbols = currentSymbols;
      }

      if (Object.keys(headingsOverride).length > 0) {
        overrides.headings_style = headingsOverride;
      }

      // 装飾（箇条書き/表）- prose_style構造に合わせる
      const proseStyleOverride = {};

      // プリセットの比較
      const baseBulletsPreset = mapBulletsPreset(
        getNested(
          baseStyle,
          'prose_style.formatting.bullets.frequency_per_1000w.typical',
        ),
      );
      if (values.bullets_preset !== baseBulletsPreset) {
        // プリセット→数値範囲に逆変換してfrequency_per_1000w.typicalに格納
        const presetToRange = {
          very_low: '0-1',
          low: '1-2',
          slightly_low: '2-3',
          medium: '2-4',
          slightly_high: '4-6',
          high: '5-8',
          very_high: '6-8',
        };
        if (!proseStyleOverride.formatting) proseStyleOverride.formatting = {};
        if (!proseStyleOverride.formatting.bullets)
          proseStyleOverride.formatting.bullets = {};
        if (!proseStyleOverride.formatting.bullets.frequency_per_1000w)
          proseStyleOverride.formatting.bullets.frequency_per_1000w = {};
        proseStyleOverride.formatting.bullets.frequency_per_1000w.typical =
          presetToRange[values.bullets_preset] || '2-4';
      }

      const baseTablesPreset = mapTablesPreset(
        getNested(
          baseStyle,
          'prose_style.formatting.tables.frequency_per_1000w.typical',
        ),
      );
      if (values.tables_preset !== baseTablesPreset) {
        const presetToRange = {
          very_low: '0-1',
          low: '1-2',
          slightly_low: '1-2',
          medium: '2-3',
          slightly_high: '3-4',
          high: '4-6',
          very_high: '5-7',
        };
        if (!proseStyleOverride.formatting) proseStyleOverride.formatting = {};
        if (!proseStyleOverride.formatting.tables)
          proseStyleOverride.formatting.tables = {};
        if (!proseStyleOverride.formatting.tables.frequency_per_1000w)
          proseStyleOverride.formatting.tables.frequency_per_1000w = {};
        proseStyleOverride.formatting.tables.frequency_per_1000w.typical =
          presetToRange[values.tables_preset] || '2-3';
      }

      // 箇条書きルール
      const baseBulletsMaxNesting = getNested(
        baseStyle,
        'prose_style.formatting.rules.bullets.max_nesting',
      );
      if (values.bullets_max_nesting !== baseBulletsMaxNesting) {
        if (!proseStyleOverride.formatting) proseStyleOverride.formatting = {};
        if (!proseStyleOverride.formatting.rules)
          proseStyleOverride.formatting.rules = {};
        if (!proseStyleOverride.formatting.rules.bullets)
          proseStyleOverride.formatting.rules.bullets = {};
        proseStyleOverride.formatting.rules.bullets.max_nesting =
          values.bullets_max_nesting;
      }

      const baseBulletsItemMaxChars = getNested(
        baseStyle,
        'prose_style.formatting.rules.bullets.item_max_chars',
      );
      if (values.bullets_item_max_chars !== baseBulletsItemMaxChars) {
        if (!proseStyleOverride.formatting) proseStyleOverride.formatting = {};
        if (!proseStyleOverride.formatting.rules)
          proseStyleOverride.formatting.rules = {};
        if (!proseStyleOverride.formatting.rules.bullets)
          proseStyleOverride.formatting.rules.bullets = {};
        proseStyleOverride.formatting.rules.bullets.item_max_chars =
          values.bullets_item_max_chars;
      }

      const baseBulletsTerminalPunct = getNested(
        baseStyle,
        'prose_style.formatting.rules.bullets.terminal_punct_disallowed',
      );
      if (
        values.bullets_terminal_punct_disallowed !== baseBulletsTerminalPunct
      ) {
        if (!proseStyleOverride.formatting) proseStyleOverride.formatting = {};
        if (!proseStyleOverride.formatting.rules)
          proseStyleOverride.formatting.rules = {};
        if (!proseStyleOverride.formatting.rules.bullets)
          proseStyleOverride.formatting.rules.bullets = {};
        proseStyleOverride.formatting.rules.bullets.terminal_punct_disallowed =
          values.bullets_terminal_punct_disallowed;
      }

      const baseBulletsParallelism = getNested(
        baseStyle,
        'prose_style.formatting.rules.bullets.parallelism_required',
      );
      if (values.bullets_parallelism_required !== baseBulletsParallelism) {
        if (!proseStyleOverride.formatting) proseStyleOverride.formatting = {};
        if (!proseStyleOverride.formatting.rules)
          proseStyleOverride.formatting.rules = {};
        if (!proseStyleOverride.formatting.rules.bullets)
          proseStyleOverride.formatting.rules.bullets = {};
        proseStyleOverride.formatting.rules.bullets.parallelism_required =
          values.bullets_parallelism_required;
      }

      if (Object.keys(proseStyleOverride).length > 0) {
        overrides.prose_style = proseStyleOverride;
      }

      // カスタムルール - prose_style.constraints構造に合わせる
      const baseInstructionsOutline =
        getNested(
          baseStyle,
          'prose_style.constraints.additional_instructions_outline',
        ) || '';
      if (values.additional_instructions_outline !== baseInstructionsOutline) {
        if (!proseStyleOverride.constraints)
          proseStyleOverride.constraints = {};
        proseStyleOverride.constraints.additional_instructions_outline =
          values.additional_instructions_outline;
      }

      const baseInstructionsContent =
        getNested(
          baseStyle,
          'prose_style.constraints.additional_instructions_content',
        ) || '';
      if (values.additional_instructions_content !== baseInstructionsContent) {
        if (!proseStyleOverride.constraints)
          proseStyleOverride.constraints = {};
        proseStyleOverride.constraints.additional_instructions_content =
          values.additional_instructions_content;
      }

      const baseAvoidPatterns =
        (
          getNested(baseStyle, 'prose_style.constraints.avoid_patterns') || []
        ).join('\n') || '';
      if (values.avoid_text !== baseAvoidPatterns) {
        if (!proseStyleOverride.constraints)
          proseStyleOverride.constraints = {};
        // テキストを配列に変換
        proseStyleOverride.constraints.avoid_patterns = values.avoid_text
          ? values.avoid_text.split('\n').filter(line => line.trim())
          : [];
      }

      // rewrite_textはbaseStyleにないので、空でない場合のみ追加
      if (values.rewrite_text && values.rewrite_text.trim()) {
        if (!proseStyleOverride.constraints)
          proseStyleOverride.constraints = {};
        // テキストを配列に変換（各行が1つの置換ルール）
        proseStyleOverride.constraints.rewrite_rules = values.rewrite_text
          .split('\n')
          .filter(line => line.trim());
      }

      if (Object.keys(proseStyleOverride).length > 0) {
        overrides.prose_style = proseStyleOverride;
      }

      // 調査フィルターオーバーライド
      const baseDomainFilter = getNested(baseStyle, 'domain_filter') || {};
      const domainFilterMode = values.domain_filter_mode || 'disabled';
      const domainFilterWhitelist = values.domain_filter_whitelist
        ? values.domain_filter_whitelist.split('\n').map(l => l.trim()).filter(l => l).slice(0, 20)
        : [];
      const domainFilterBlacklist = values.domain_filter_blacklist
        ? values.domain_filter_blacklist.split('\n').map(l => l.trim()).filter(l => l).slice(0, 20)
        : [];
      
      // 変更がある場合のみオーバーライドに追加
      const baseMode = baseDomainFilter.mode || 'disabled';
      const baseWhitelist = (baseDomainFilter.whitelist || []).join('\n');
      const baseBlacklist = (baseDomainFilter.blacklist || []).join('\n');
      
      if (
        domainFilterMode !== baseMode ||
        domainFilterWhitelist.join('\n') !== baseWhitelist ||
        domainFilterBlacklist.join('\n') !== baseBlacklist
      ) {
        overrides.domain_filter_override = {
          mode: domainFilterMode,
          whitelist: domainFilterWhitelist,
          blacklist: domainFilterBlacklist,
        };
      }

      onSave(overrides);
    } catch (error) {
      console.error('Form validation error:', error);
    }
  };

  const handleReset = () => {
    form.resetFields();
    onSave(null);
  };

  return (
    <Modal
      title={
        <Space>
          <SettingOutlined />
          <span>スタイル微調整：{styleName}</span>
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      width={1000}
      style={{ top: 20 }}
      bodyStyle={{
        maxHeight: 'calc(100vh - 200px)',
        overflowY: 'auto',
        paddingBottom: 24,
      }}
      footer={[
        <Button key="reset" onClick={handleReset}>
          リセット
        </Button>,
        <Button key="cancel" onClick={onCancel}>
          キャンセル
        </Button>,
        <Button key="save" type="primary" onClick={handleSave}>
          適用
        </Button>,
      ]}
    >
      <Alert
        type="info"
        message="この画面での変更は今回の生成のみに適用されます。元のスタイル設定は変更されません。"
        style={{ marginBottom: 16 }}
        showIcon
      />

      <Form form={form} layout="vertical">
        <Collapse defaultActiveKey={['meta', 'decoration', 'rules']}>
          {/* 見出し設定 */}
          <Panel header={<Text strong>見出し設定</Text>} key="meta">
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item name="h2_typical" label="H2の個数目安">
                  <Input placeholder="例: 6-10" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="h3_typical" label="H3の個数目安">
                  <Input placeholder="例: 2-4" />
                </Form.Item>
              </Col>
            </Row>
            <Divider style={{ margin: '16px 0' }} />
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item
                  name="h2_chars_typical"
                  label="H2見出しの文字数"
                  tooltip="見出し自体の文字数（例: 16-28）"
                >
                  <Input placeholder="例: 16-28" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="h3_chars_typical"
                  label="H3見出しの文字数"
                  tooltip="見出し自体の文字数（例: 14-24）"
                >
                  <Input placeholder="例: 14-24" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item
                  name="h2_body_chars_typical"
                  label="H2直下の本文の文字数目安"
                  tooltip="最大800文字まで設定可能"
                  rules={[
                    {
                      validator: (_, value) => {
                        if (!value) return Promise.resolve();
                        const nums = value.match(/\d+/g);
                        if (nums && nums.some(n => parseInt(n, 10) > 800)) {
                          return Promise.reject(
                            new Error('最大800文字までです'),
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input
                    placeholder="例: 400-800"
                    onBlur={e => {
                      let val = (e.target.value || '').replace(/[^0-9-]/g, '');
                      const nums = val.match(/\d+/g);
                      if (nums) {
                        const capped = nums.map(n =>
                          Math.min(parseInt(n, 10), 800),
                        );
                        if (val.includes('-') && capped.length === 2) {
                          val = `${capped[0]}-${capped[1]}`;
                        } else if (capped.length >= 1) {
                          val = val.includes('-')
                            ? `${capped[0]}-${capped[capped.length - 1] || capped[0]}`
                            : String(capped[0]);
                        }
                      }
                      if (val !== e.target.value) {
                        form.setFieldsValue({ h2_body_chars_typical: val });
                      }
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="h3_body_chars_typical"
                  label="H3直下の本文の文字数目安"
                  tooltip="最大800文字まで設定可能"
                  rules={[
                    {
                      validator: (_, value) => {
                        if (!value) return Promise.resolve();
                        const nums = value.match(/\d+/g);
                        if (nums && nums.some(n => parseInt(n, 10) > 800)) {
                          return Promise.reject(
                            new Error('最大800文字までです'),
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input
                    placeholder="例: 300-500"
                    onBlur={e => {
                      let val = (e.target.value || '').replace(/[^0-9-]/g, '');
                      const nums = val.match(/\d+/g);
                      if (nums) {
                        const capped = nums.map(n =>
                          Math.min(parseInt(n, 10), 800),
                        );
                        if (val.includes('-') && capped.length === 2) {
                          val = `${capped[0]}-${capped[1]}`;
                        } else if (capped.length >= 1) {
                          val = val.includes('-')
                            ? `${capped[0]}-${capped[capped.length - 1] || capped[0]}`
                            : String(capped[0]);
                        }
                      }
                      if (val !== e.target.value) {
                        form.setFieldsValue({ h3_body_chars_typical: val });
                      }
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item
                  name="ui_allow_interrogative"
                  label="見出しに疑問文を使う"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="ui_heading_symbols" label="見出しの許可記号">
                  <Checkbox.Group
                    options={[
                      {
                        label: 'クエスチョン（？）',
                        value: 'question',
                      },
                      {
                        label: 'かっこ（（）[]）',
                        value: 'brackets',
                      },
                      { label: 'コロン（:）', value: 'colon' },
                    ]}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Panel>

          {/* 装飾（箇条書き/表） */}
          <Panel
            header={<Text strong>装飾（箇条書き/表）</Text>}
            key="decoration"
          >
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item label="箇条書きの頻度" name="bullets_preset">
                  <Select
                    options={[
                      { label: 'ほとんどなし', value: 'very_low' },
                      { label: '少なめ', value: 'low' },
                      { label: 'やや少なめ', value: 'slightly_low' },
                      { label: '標準', value: 'medium' },
                      { label: 'やや多め', value: 'slightly_high' },
                      { label: '多め', value: 'high' },
                      { label: 'かなり多め', value: 'very_high' },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="表の頻度" name="tables_preset">
                  <Select
                    options={[
                      { label: 'ほとんどなし', value: 'very_low' },
                      { label: '少なめ', value: 'low' },
                      { label: 'やや少なめ', value: 'slightly_low' },
                      { label: '標準', value: 'medium' },
                      { label: 'やや多め', value: 'slightly_high' },
                      { label: '多め', value: 'high' },
                      { label: 'かなり多め', value: 'very_high' },
                    ]}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Divider style={{ margin: '12px 0' }}>箇条書きルール</Divider>
            <Row gutter={12}>
              <Col xs={24} md={12}>
                <Form.Item
                  label="入れ子の深さ（max）"
                  name="bullets_max_nesting"
                  tooltip="推奨: 0〜2"
                >
                  <InputNumber min={0} max={5} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="1項目の最大文字数"
                  name="bullets_item_max_chars"
                  tooltip="推奨: 80"
                >
                  <InputNumber min={20} max={200} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={12}>
              <Col xs={24} md={12}>
                <Form.Item
                  label="行末の句点を付けない"
                  name="bullets_terminal_punct_disallowed"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="並列性を揃える（語尾/形式）"
                  name="bullets_parallelism_required"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
            </Row>
          </Panel>

          {/* カスタムルール */}
          <Panel
            header={<Text strong>カスタムルール（任意）</Text>}
            key="rules"
          >
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <Row gutter={12}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="追加指示（構成案）"
                    name="additional_instructions_outline"
                    extra="構成案生成時の追加指示（最大1,000文字）"
                  >
                    <TextArea
                      rows={3}
                      maxLength={1000}
                      showCount
                      placeholder="例：各見出しに必ず具体例を含める。数値データを優先的に配置する。"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="追加指示（本文）"
                    name="additional_instructions_content"
                    extra="本文執筆時の追加指示（最大1,000文字）"
                  >
                    <TextArea
                      rows={3}
                      maxLength={1000}
                      showCount
                      placeholder="例：専門用語は必ず初出時に解説する。数値データを積極的に引用する。"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={12}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="禁止表現"
                    name="avoid_text"
                    extra="1行1ルール（最大500文字）"
                  >
                    <TextArea
                      rows={4}
                      maxLength={500}
                      showCount
                      placeholder={
                        '例:\n非常に非常に\n〜と言えるでしょう。でしょう。'
                      }
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="置換ルール"
                    name="rewrite_text"
                    extra="A=>B形式、1行1ルール（最大500文字）"
                  >
                    <TextArea
                      rows={4}
                      maxLength={500}
                      showCount
                      placeholder={
                        '例:\n非常に=>かなり\n〜だと思います=>〜です'
                      }
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Space>
          </Panel>
          
          {/* 調査フィルター */}
          <Panel
            header={
              <Space>
                <span>調査フィルター</span>
                <Tooltip title="Web調査時のドメイン制限">
                  <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                </Tooltip>
              </Space>
            }
            key="domain_filter"
          >
            <Space direction="vertical" style={{ width: '100%' }} size={12}>
              <Form.Item
                label="フィルターモード"
                name="domain_filter_mode"
              >
                <Radio.Group>
                  <Radio value="disabled">無効</Radio>
                  <Radio value="whitelist">ホワイトリスト（指定ドメインのみ検索）</Radio>
                  <Radio value="blacklist">ブラックリスト（指定ドメインを除外）</Radio>
                </Radio.Group>
              </Form.Item>
              <Form.Item noStyle shouldUpdate={(prev, cur) => prev.domain_filter_mode !== cur.domain_filter_mode}>
                {({ getFieldValue }) => {
                  const mode = getFieldValue('domain_filter_mode') || 'disabled';
                  const whitelistOpacity = mode === 'whitelist' ? 1 : 0.4;
                  const blacklistOpacity = mode === 'blacklist' ? 1 : 0.4;
                  return (
                    <Row gutter={12}>
                      <Col xs={24} md={12} style={{ opacity: whitelistOpacity, transition: 'opacity 0.2s' }}>
                        <Form.Item
                          label="ホワイトリスト"
                          name="domain_filter_whitelist"
                          extra="1行1ドメイン（最大20件）"
                        >
                          <TextArea
                            rows={4}
                            placeholder={"example.com\nwikipedia.org\n.gov"}
                            disabled={mode !== 'whitelist'}
                            onBlur={(e) => {
                              const processed = processDomainInput(e.target.value);
                              form.setFieldsValue({ domain_filter_whitelist: processed });
                            }}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12} style={{ opacity: blacklistOpacity, transition: 'opacity 0.2s' }}>
                        <Form.Item
                          label="ブラックリスト"
                          name="domain_filter_blacklist"
                          extra="1行1ドメイン（最大20件）"
                        >
                          <TextArea
                            rows={4}
                            placeholder={"reddit.com\npinterest.com\nquora.com"}
                            disabled={mode !== 'blacklist'}
                            onBlur={(e) => {
                              const processed = processDomainInput(e.target.value);
                              form.setFieldsValue({ domain_filter_blacklist: processed });
                            }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  );
                }}
              </Form.Item>
              <div
                style={{
                  background: '#fffbe6',
                  border: '1px solid #fff1b8',
                  borderRadius: 6,
                  padding: '8px 12px',
                  fontSize: 12,
                  color: '#8b7355',
                }}
              >
                URLを貼り付けるとドメインを自動抽出 / TLD指定可（.gov, .edu等） / 選択モードのリストのみ有効
              </div>
            </Space>
          </Panel>
        </Collapse>
      </Form>
    </Modal>
  );
};

export default StyleOverrideModal;
